import { useEffect, useState } from "react";
import { gw } from "./gateway.js";
import { resumeDebate } from "./api.js";
import { useRoute } from "./router.js";
import Topics from "./screens/Topics.jsx";
import Stance from "./screens/Stance.jsx";
import Debate from "./screens/Debate.jsx";
import Conviction from "./screens/Conviction.jsx";
import Result from "./screens/Result.jsx";
import Resume from "./screens/Resume.jsx";
import Archive from "./screens/Archive.jsx";
import MyDebates from "./screens/MyDebates.jsx";
import Privacy from "./screens/Privacy.jsx";
import Booting from "./screens/Booting.jsx";
import Broken from "./screens/Broken.jsx";
import LoginGate from "./ui/LoginGate.jsx";

export default function App() {
  const [path, go] = useRoute();
  const [boot, setBoot] = useState({ state: "loading" });

  // 진행 중인 토론(서버가 아는 것). 클라이언트는 이걸 들고만 있고 판정하지 않는다.
  const [session, setSession] = useState(null);
  // 주제·입장은 아직 서버에 없는 화면 간 선택값이다.
  const [draft, setDraft] = useState(null);
  // 로그인 여부를 화면이 알아야 한다. 권한 판정은 여전히 서버가 한다.
  const [user, setUser] = useState(null);
  // 지난 기록에서 열어 본 결과. 진행 중 토론(session)과 섞이면 안 된다.
  const [past, setPast] = useState(null);

  useEffect(() => gw.onChange(setUser), []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        await gw.ensureSignedIn();
        // 익명이면 진행 중 토론이 있을 수 없다. 물으면 서버가 거부한다.
        if (!gw.isAnonymous()) {
          const { debate, turns } = await resumeDebate();
          if (!alive) return;
          if (debate) setSession({ debate, turns });
        }
        setBoot({ state: "ready" });
      } catch (err) {
        if (alive) setBoot({ state: "failed", err });
      }
    })();
    return () => { alive = false; };
  }, []);

  if (boot.state === "loading") return <Booting />;
  if (boot.state === "failed") {
    return <Broken err={boot.err} onRetry={() => window.location.reload()} />;
  }

  // 하던 토론이 있으면 새 주제를 고르기 전에 먼저 보여준다.
  // 한도는 이미 그 토론에 나갔으므로, 버리면 오늘은 더 못 한다.
  if (path === "/" && session && session.debate.status === "in_progress") {
    return <Resume session={session} onContinue={() => go("/debate")} onLeave={() => go("/archive")} />;
  }

  /**
   * 토론과 기록은 로그인한 사람만. 화면은 편의일 뿐이고,
   * 실제 차단은 서버(requireUser)가 한다.
   */
  const NEEDS_LOGIN = {
    "/stance": ["토론을 시작하려면 로그인해 주세요", "여섯 번 주고받는 동안 매 턴이 저장돼요. 계정이 있어야 이어서 할 수 있어요."],
    "/debate": ["로그인이 필요해요", "하던 토론은 계정에 남아 있어요."],
    "/conviction": ["로그인이 필요해요", "하던 토론은 계정에 남아 있어요."],
    "/me": ["지난 토론을 보려면 로그인해 주세요", "계정에 남은 기록을 여기서 다시 볼 수 있어요."],
    "/me/result": ["로그인이 필요해요", "계정에 남은 기록을 여기서 다시 볼 수 있어요."],
    "/privacy": ["개인정보 설정은 로그인한 뒤에 볼 수 있어요", "무엇에 동의했는지, 어떻게 그만둘지는 계정에 붙어 있어요."],
  };
  if (gw.isAnonymous() && NEEDS_LOGIN[path]) {
    const [title, lead] = NEEDS_LOGIN[path];
    return <LoginGate title={title} lead={lead} onBack={() => go("/")} onDone={() => go(path, { replace: true })} />;
  }

  switch (path) {
    case "/":
      return (
        <Topics
          user={user}
          onMyDebates={() => go("/me")}
          onPick={(topic) => { setDraft({ topic }); go("/stance"); }}
          // 익명이면 /stance 에서 LoginGate 가 받는다. draft 는 남으므로 로그인 후 그대로 이어진다.
          // 로그인·로그아웃하면 uid 가 바뀐다 — 진행 중 토론을 다시 물어야 한다.
          onAuthChanged={async () => {
            const { debate, turns } = await resumeDebate();
            setSession(debate ? { debate, turns } : null);
          }}
        />
      );

    case "/stance":
      if (!draft?.topic) { go("/", { replace: true }); return <Booting />; }
      return (
        <Stance
          topic={draft.topic}
          onBack={() => go("/")}
          onStarted={(debate) => { setSession({ debate, turns: [] }); go("/debate"); }}
        />
      );

    case "/debate":
      if (!session) { go("/", { replace: true }); return <Booting />; }
      return (
        <Debate
          session={session}
          onTurns={(next) => setSession((s) => ({ ...s, turns: next }))}
          onDone={() => go("/conviction")}
          onLeave={() => go("/")}
        />
      );

    case "/conviction":
      if (!session) { go("/", { replace: true }); return <Booting />; }
      return (
        <Conviction
          session={session}
          onFinished={(outcome) => {
            setSession((s) => ({ ...s, outcome }));
            go("/result");
          }}
        />
      );

    case "/archive":
      return <Archive onStart={() => go("/")} />;

    case "/privacy":
      return <Privacy onBack={() => go("/me")} />;

    case "/me":
      return (
        <MyDebates
          onPrivacy={() => go("/privacy")}
          onHome={() => go("/")}
          onOpen={(d) => {
            // 저장된 토론 문서를 결과 화면이 아는 모양으로 맞춘다.
            setPast({
              debate: d,
              outcome: { outcome: d.outcome, convictionEnd: d.convictionEnd, evasions: d.evasions ?? 0 },
            });
            go("/me/result");
          }}
        />
      );

    case "/me/result":
      if (!past) { go("/me", { replace: true }); return <Booting />; }
      return (
        <Result
          session={past}
          // 올린 걸 내리는 길이 여기밖에 없다. 지난 결과에서도 열 수 있어야 한다.
          showSave
          onBack={() => { setPast(null); go("/me"); }}
          onNew={() => { setPast(null); go("/"); }}
        />
      );

    case "/result":
      if (!session?.outcome) { go("/", { replace: true }); return <Booting />; }
      return <Result session={session} onNew={() => { setSession(null); setDraft(null); go("/"); }} />;

    default:
      return (
        <Broken
          message="그런 주소는 없어요"
          onRetry={() => go("/")}
          retryLabel="처음으로"
        />
      );
  }
}
